import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import QrCard from "../components/QrCard";
import SafeHtml from "../components/SafeHtml";
import { CircleNotch, ArrowLeft, WarningCircle, Gauge, CheckCircle, MagnifyingGlass, ArrowSquareOut, GoogleLogo, YoutubeLogo, Globe, Sparkle } from "@phosphor-icons/react";

// Minimal, safe Markdown -> HTML for AI guidance (headings, bold, lists, paragraphs).
function mdToHtml(md) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => esc(s)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`(.+?)`/g, "<code>$1</code>");
  const lines = (md || "").split("\n");
  const out = [];
  let list = null; // 'ul' | 'ol'
  const closeList = () => { if (list) { out.push(`</${list}>`); list = null; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    let m;
    if ((m = line.match(/^#{1,6}\s+(.*)/))) {
      closeList(); out.push(`<h4>${inline(m[1])}</h4>`);
    } else if ((m = line.match(/^\s*[-*]\s+(.*)/))) {
      if (list !== "ul") { closeList(); list = "ul"; out.push("<ul>"); }
      out.push(`<li>${inline(m[1])}</li>`);
    } else if ((m = line.match(/^\s*\d+[.)]\s+(.*)/))) {
      if (list !== "ol") { closeList(); list = "ol"; out.push("<ol>"); }
      out.push(`<li>${inline(m[1])}</li>`);
    } else if (line.trim() === "") {
      closeList();
    } else {
      closeList(); out.push(`<p>${inline(line)}</p>`);
    }
  }
  closeList();
  return out.join("");
}

const SEARCH_ENGINES = [
  { id: "google", label: "Google", icon: GoogleLogo, url: (q) => `https://www.google.com/search?q=${q}` },
  { id: "bing", label: "Bing", icon: Globe, url: (q) => `https://www.bing.com/search?q=${q}` },
  { id: "ddg", label: "DuckDuckGo", icon: MagnifyingGlass, url: (q) => `https://duckduckgo.com/?q=${q}` },
  { id: "youtube", label: "YouTube", icon: YoutubeLogo, url: (q) => `https://www.youtube.com/results?search_query=${q}` },
];

function ResearchCard({ data }) {
  const manufacturer = data.manufacturer || "";
  const model = data.model_number || "";
  const serial = data.serial_number || "";
  const searchTerms = [
    manufacturer && `manufacturer ${manufacturer}`,
    model && `Model ${model}`,
    serial && `serial number ${serial}`,
  ].filter(Boolean).join(" ").trim();
  const hasTerms = searchTerms.length > 0;
  const query = encodeURIComponent(searchTerms);
  const open = (engine) => window.open(engine.url(query), "_blank", "noopener,noreferrer");

  const [fault, setFault] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState("");
  const [aiError, setAiError] = useState("");

  const runAI = async () => {
    setAiLoading(true); setAiError(""); setAiResult("");
    try {
      const { data: res } = await api.post("/assets/ai-research", {
        manufacturer, model, serial_number: serial,
        asset_name: data.asset_name, category: data.category_name, fault,
      });
      setAiResult(res.guidance || "");
    } catch (e) {
      setAiError(e.response?.data?.detail || "AI research failed. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col rounded-sm border border-neutral-200 bg-white p-5 shadow-sm" data-testid="asset-research-card">
      <h3 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-[#0A0A0A]">
        <MagnifyingGlass size={18} weight="bold" className="text-[#0055FF]" /> Research this asset
      </h3>
      <p className="mt-1 text-sm text-neutral-500">
        Search the web for manuals, repair guides and datasheets, or ask AI for guidance.
      </p>

      <div className="mt-3 space-y-1.5 rounded-sm bg-neutral-50 p-3" data-testid="asset-research-terms">
        {[["Manufacturer", manufacturer], ["Model", model], ["Serial number", serial]].map(([k, v]) => (
          <div key={k} className="flex gap-3 text-sm">
            <span className="w-28 shrink-0 text-xs uppercase tracking-wider text-neutral-400">{k}</span>
            <span className="font-medium text-[#0A0A0A]">{v || "—"}</span>
          </div>
        ))}
      </div>

      {!hasTerms ? (
        <p className="mt-4 rounded-sm bg-[#D97706]/10 px-3 py-2 text-xs font-semibold text-[#D97706]" data-testid="asset-research-empty">
          No manufacturer, model or serial on this asset to search with.
        </p>
      ) : (
        <>
          {/* Web search engines — single row */}
          <p className="mt-4 mb-1.5 text-xs uppercase tracking-wider text-neutral-400">Web search</p>
          <div className="flex flex-wrap gap-2" data-testid="asset-research-engines">
            {SEARCH_ENGINES.map((e) => (
              <button
                key={e.id}
                data-testid={`asset-research-${e.id}`}
                onClick={() => open(e)}
                className="inline-flex items-center gap-1.5 rounded-sm border border-neutral-300 px-3 py-1.5 text-xs font-semibold text-neutral-700 transition-all duration-200 hover:border-[#0055FF] hover:text-[#0055FF]"
              >
                <e.icon size={14} weight="bold" /> {e.label}
                <ArrowSquareOut size={11} weight="bold" className="text-neutral-400" />
              </button>
            ))}
          </div>

          {/* AI search */}
          <div className="mt-5 border-t border-neutral-100 pt-4">
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#7C3AED]">
              <Sparkle size={14} weight="fill" /> AI assistant
            </p>
            <p className="mt-1 text-xs text-neutral-500">
              Add a fault or symptom (optional) for likely causes &amp; fixes — or leave blank for a general overview.
            </p>
            <textarea
              data-testid="asset-research-fault-input"
              value={fault}
              onChange={(ev) => setFault(ev.target.value)}
              rows={2}
              placeholder="e.g. door opens but won't close fully; motor runs but no movement…"
              className="mt-2 w-full resize-y rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#7C3AED]"
            />
            <button
              data-testid="asset-research-ai-btn"
              onClick={runAI}
              disabled={aiLoading}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-sm bg-[#7C3AED] px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#6B21C8] disabled:opacity-60"
            >
              {aiLoading ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
              {aiLoading ? "Researching…" : "Ask AI about this asset"}
            </button>

            {aiError && <p className="mt-2 text-xs font-semibold text-[#DC2626]" data-testid="asset-research-ai-error">{aiError}</p>}
            {aiResult && (
              <div className="mt-3 rounded-sm border border-[#7C3AED]/20 bg-[#7C3AED]/[0.04] p-3" data-testid="asset-research-ai-result">
                <SafeHtml html={mdToHtml(aiResult)} className="prose-asset text-sm leading-relaxed text-neutral-800" />
                <p className="mt-3 border-t border-[#7C3AED]/15 pt-2 text-[11px] text-neutral-400">
                  AI-generated guidance — always verify part numbers and specs with the manufacturer before acting.
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  return String(s).replace("T", " ").slice(0, 19);
}

function conditionTone(value) {
  const n = parseInt(value, 10);
  if (!n) return "bg-neutral-100 text-neutral-500";
  if (n >= 4) return "bg-[#DC2626]/10 text-[#DC2626]";
  if (n === 3) return "bg-[#D97706]/10 text-[#D97706]";
  return "bg-[#059669]/10 text-[#059669]";
}

function ConditionPanel({ assetId }) {
  const [data, setData] = useState(undefined);
  useEffect(() => {
    api.get(`/assets/condition`, { params: { asset_id: assetId } })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [assetId]);

  const cond = data?.condition;
  const history = data?.history || [];
  // Union of field names across all readings, ordered by the latest reading.
  const fieldNames = [];
  (cond?.fields || []).forEach((f) => { if (f.name && !fieldNames.includes(f.name)) fieldNames.push(f.name); });
  history.forEach((h) => (h.fields || []).forEach((f) => { if (f.name && !fieldNames.includes(f.name)) fieldNames.push(f.name); }));
  const shortName = (n) => (n || "").split("(")[0].trim();
  const valOf = (reading, name) => {
    const f = (reading.fields || []).find((x) => x.name === name);
    return f ? f.value : null;
  };

  return (
    <div className="mt-6 rounded-sm border border-neutral-200 bg-white p-5 shadow-sm" data-testid="asset-condition-panel">
      <div className="flex items-center gap-2">
        <Gauge size={20} weight="bold" className="text-[#0055FF]" />
        <h2 className="font-heading text-xl font-black tracking-tight text-[#0A0A0A]">Condition Report</h2>
        {history.length > 0 && (
          <span className="rounded-sm bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-600" data-testid="condition-history-count">
            {history.length} reading{history.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
      {data === undefined ? (
        <div className="mt-3 flex h-20 items-center justify-center"><CircleNotch size={22} className="animate-spin text-[#0055FF]" /></div>
      ) : !cond ? (
        <p className="mt-2 text-sm text-neutral-500" data-testid="asset-condition-empty">
          No condition report imported for this asset yet. Condition reports are captured by technicians on jobs and imported automatically each night.
        </p>
      ) : (
        <div className="mt-3">
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-neutral-500">Latest reading</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {(cond.fields || []).map((f, i) => (
              <div key={i} className="rounded-sm border border-neutral-200 p-3" data-testid={`condition-field-${i}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{f.name}</p>
                <span className={`mt-1.5 inline-flex rounded-sm px-2.5 py-1 text-lg font-black ${conditionTone(f.value)}`}>
                  {f.value != null && f.value !== "" ? f.value : "—"}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-1 text-sm text-neutral-600">
            <span>Checked by: <span className="font-semibold text-[#0A0A0A]">{cond.checked_by || "—"}</span></span>
            <span>Date checked: <span className="font-semibold text-[#0A0A0A]">{fmtDate(cond.datetime_checked)}</span></span>
            {cond.compliance_status && <span>Compliance: <span className="font-semibold text-[#0A0A0A]">{cond.compliance_status}</span></span>}
            {cond.service_action && <span>Service action: <span className="font-semibold text-[#0A0A0A]">{cond.service_action}</span></span>}
            <span>Source job: <span className="font-mono text-xs">{cond.job_number}</span></span>
          </div>

          {/* History — how the condition has changed over time */}
          <p className="mb-2 mt-6 text-xs font-bold uppercase tracking-widest text-neutral-500">History over time</p>
          <div className="overflow-x-auto border border-neutral-200 rounded-sm" data-testid="condition-history-table">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  <th className="px-3 py-2.5 text-left">Date checked</th>
                  {fieldNames.map((n) => <th key={n} className="px-3 py-2.5 text-center" title={n}>{shortName(n)}</th>)}
                  <th className="px-3 py-2.5 text-left">Checked by</th>
                  <th className="px-3 py-2.5 text-left">Service action</th>
                  <th className="px-3 py-2.5 text-left">Job</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, ri) => (
                  <tr key={ri} data-testid={`condition-history-row-${ri}`} className="border-b border-neutral-100 even:bg-neutral-50/60">
                    <td className="whitespace-nowrap px-3 py-2.5 text-neutral-700">{fmtDate(h.datetime_checked) === "—" ? fmtDate(h.updated_at) : fmtDate(h.datetime_checked)}</td>
                    {fieldNames.map((n) => {
                      const v = valOf(h, n);
                      return (
                        <td key={n} className="px-3 py-2.5 text-center">
                          <span className={`inline-flex min-w-[28px] justify-center rounded-sm px-2 py-0.5 text-sm font-bold ${conditionTone(v)}`}>
                            {v != null && v !== "" ? v : "—"}
                          </span>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2.5 text-neutral-700">{h.checked_by || "—"}</td>
                    <td className="px-3 py-2.5 text-neutral-700">{h.service_action || "—"}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-neutral-500">{h.job_number}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-2 flex items-center gap-1 text-xs text-neutral-400">
            <CheckCircle size={12} weight="fill" className="text-[#059669]" /> Imported {fmtDate(data.last_synced)}
          </p>
        </div>
      )}
    </div>
  );
}

function DetailRow({ k, v }) {
  let display = v;
  if (v === null || v === undefined || v === "") display = "—";
  else if (typeof v === "object") display = JSON.stringify(v);
  else if (typeof v === "boolean") display = v ? "Yes" : "No";
  return (
    <div className="flex gap-3 border-b border-neutral-100 py-2 last:border-0">
      <span className="w-40 shrink-0 font-mono text-xs text-neutral-500">{k}</span>
      <span className="min-w-0 break-words text-sm text-[#0A0A0A]">{String(display)}</span>
    </div>
  );
}

export default function AssetDetail() {
  const [params] = useSearchParams();
  const assetId = params.get("id");
  const [data, setData] = useState(undefined);

  useEffect(() => {
    if (!assetId) { setData(null); return; }
    api.get(`/assets/detail`, { params: { asset_id: assetId } })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [assetId]);

  const raw = data?.raw || {};
  const customFields = raw.customfields || [];

  return (
    <Layout>
      <div data-testid="asset-detail-page">
        <Link to="/asset-list" data-testid="asset-detail-back"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-500 hover:text-[#0055FF]">
          <ArrowLeft size={15} weight="bold" /> Back to Asset List
        </Link>

        {data === undefined ? (
          <div className="mt-10 flex h-40 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
        ) : data === null ? (
          <div className="mt-10 flex h-56 flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-neutral-300 bg-white" data-testid="asset-detail-error">
            <WarningCircle size={28} className="text-[#D97706]" />
            <p className="text-sm text-neutral-600">Asset not found.</p>
          </div>
        ) : (
          <>
            <p className="mt-3 text-sm uppercase tracking-widest font-semibold text-neutral-500">Asset detail</p>
            <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" data-testid="asset-detail-name">
              {data.asset_name || "Asset"}
            </h1>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <QrCard
                testid="asset-qr-card"
                apiPath={`/assets/qr?asset_id=${encodeURIComponent(assetId)}`}
                targetUrl={`${window.location.origin}/asset-detail?id=${encodeURIComponent(assetId)}`}
                downloadName={`asset-${(data.barcode || data.serial_number || "qr")}.jpg`}
                title=""
                description="Anyone who scans this code is taken to this asset's detail page. They'll be asked to sign in first if they aren't already."
              />
              <ResearchCard data={data} />
            </div>

            <ConditionPanel assetId={assetId} />

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="rounded-sm border border-neutral-200 bg-white p-5 shadow-sm">
                <h2 className="mb-2 text-sm font-bold uppercase tracking-widest text-neutral-500">Summary</h2>
                {[
                  ["Client", data.client_name], ["Asset type", data.category_name],
                  ["Business unit", data.org_name], ["Barcode", data.barcode],
                  ["Serial number", data.serial_number], ["Model", data.model_number],
                  ["Manufacturer", data.manufacturer], ["Quantity", data.quantity],
                  ["Cost", data.cost], ["Archived", data.archived ? "Yes" : "No"],
                  ["Status", data.status], ["Last updated", fmtDate(data.last_updated)],
                ].map(([k, v]) => <DetailRow key={k} k={k} v={v} />)}
              </div>

              <div className="rounded-sm border border-neutral-200 bg-white p-5 shadow-sm">
                <h2 className="mb-2 text-sm font-bold uppercase tracking-widest text-neutral-500">Custom fields</h2>
                {customFields.length === 0 ? (
                  <p className="text-sm text-neutral-500">No custom fields on this asset.</p>
                ) : (
                  customFields.map((cf, i) => (
                    <DetailRow key={i} k={cf.name || cf.label || `field_${i}`} v={cf.value ?? cf.fieldvalue ?? JSON.stringify(cf)} />
                  ))
                )}
              </div>
            </div>

            <div className="mt-6 rounded-sm border border-neutral-200 bg-white p-5 shadow-sm">
              <h2 className="mb-2 text-sm font-bold uppercase tracking-widest text-neutral-500">All fields (raw)</h2>
              <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-2">
                {Object.entries(raw).map(([k, v]) => <DetailRow key={k} k={k} v={v} />)}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
